import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import GraphqlApi from '../../api-graphql';
import { Storage } from 'aws-amplify';
import { flatten } from 'flat';
import './styles.css';
import { LinearProgress } from '@material-ui/core';
import useWindowSize from '../../customHooks/useWindowSize';
import TemplateWithGrid from './TemplateWithGrid';
import TemplateWithDialog from './TemplateWithDialog';
import JSZip from 'jszip';
import Docxtemplater from 'docxtemplater';
import { useStyles } from './styles';
import { useHistory } from 'react-router-dom';
import CustomForm from '../Form';
import LoadingCard from '../UI/LoadingCard';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { CONSTANTS } from '../../constants/constants';

const MIMETYPE_DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const Template = (props) => {
  const classes = useStyles();
  const windowSize = useWindowSize();
  const [loading, setLoading] = useState(true);
  const [schemas, setSchemas] = useState([]);
  const [uiSchemas, setUiSchemas] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [submitOk, setSubmitOk] = useState(false);
  const [template, setTemplate] = useState(undefined);
  const [creatingDoc, setCreatingDoc] = useState(false);
  const [schema, setSchema] = useState({});
  const [uiSchema, setUiSchema] = useState({});
  const [errorList, setErrorList] = useState([]);
  const [message, setMessage] = useState();
  const mainFormRef = useRef(null);
  const submitMainFormRef = useRef(null);
  const { t } = useTranslation();

  const { user, group, subgroup } = props;

  const history = useHistory();
  useEffect(() => {
    GraphqlApi.getTemplate(props.match.params.number).then((result) => {
      setTemplate(result.data.getTemplate);
      setSchema(JSON.parse(result.data.getTemplate.schema));
      setSchemas(schemaForProperties(JSON.parse(result.data.getTemplate.schema)));
      setUiSchema(JSON.parse(result.data.getTemplate.uischema));
      setUiSchemas(JSON.parse(result.data.getTemplate.uischema));
      setLoading(false);
    });

    if (typeof expanded === 'string' && submitOk) {
      document.getElementById(`btn_${expanded}`).click();
      setSubmitOk(false);
    }
  }, []);

  const schemaForProperties = (schema) => {
    var listSchemaProperties = [];
    var schemaProperties = {};
    var { title, description, properties, required, type } = schema;
    var first = true;

    for (const prop in properties) {
      if (first) {
        schemaProperties = schemaPushBasicData(title, description, prop, required, type);
        first = false;
      } else {
        schemaProperties = schemaPushBasicData(null, null, prop, required, type);
      }

      const myObj = {};
      myObj[prop] = properties[prop];
      schemaProperties.schema.properties = myObj;
      listSchemaProperties.push(schemaProperties);
    }
    return listSchemaProperties;
  };

  const schemaPushBasicData = (title, description, name, required, type) => {
    var newSchemaProperties = {};
    var dataObject = {};
    if (title && description) {
      newSchemaProperties = {
        title: title,
        description: description,
        required: required,
        type: type
      };
    } else {
      newSchemaProperties = {
        required: required,
        type: type
      };
    }
    dataObject = {
      name: name,
      message: false,
      schema: newSchemaProperties
    };
    return dataObject;
  };

  const parseData = (formData) => {
    let data = {
      ...formData,
      ...(flatten(formData, { delimiter: '_' }))
    };
    return data;
  };

  const buildInfo = (user, group) => {
    const date = new Date();
    const id = `${date.getTime()}_${user.username}`;
    const fileUrl = `document/${group}/${id}/${id}.docx`;
    const info = { date, id, fileUrl };
    return info;
  };

  const loadDocx = async (formData, template) => {
    const s3Response = await Storage.get(template.fileUrl, { download: true });
    const initialDocx = await s3Response.Body.arrayBuffer();
    return initialDocx;
  };

  const settingDocx = (initialDocx, data) => {
    const zip = new JSZip(initialDocx);
    const options = { paragraphLoop: true, linebreaks: true };

    let workingDocx = new Docxtemplater(zip, options);
    workingDocx.setData(data);

    try {
      workingDocx.render();
    } catch (error) {
      docxRenderError(error);
    }

    return workingDocx;
  };

  const buildFinalDocx = (user, group, workingDocx) => {
    const finalDocx = workingDocx.getZip().generate({
      type: 'blob',
      mimeType: MIMETYPE_DOCX
    });
    return finalDocx;
  };

  const sendForm = async (formData) => {
    try {
      const data = parseData(formData);
      const info = buildInfo(user, group);
      const initialDocx = await loadDocx(data, template);
      const workingDocx = settingDocx(initialDocx, data);
      const finalDocx = buildFinalDocx(user, group, workingDocx);

      await Storage.put(info.fileUrl, finalDocx, {
        contentType: MIMETYPE_DOCX
      });

      const input = {
        id: info.id,
        name: formData.documento.nombre,
        description: formData.documento.descripcion || '',
        group,
        fileUrl: info.fileUrl,
        data: JSON.stringify(data),
        status: CONSTANTS.DOCUMENT.STATUS.INIT,
        documentTemplateId: template.id,
        createdAt: info.date.toISOString(),
        type: 'document',
        principal: {
          name: formData.documento.nombre,
          path: info.fileUrl,
          description: formData.documento.descripcion,
          signaturesPositions: template.signaturesPositions
        }
      };

      await GraphqlApi.createDocument(input, subgroup);
      setMessage('Ready');
      history.push('/document');
    } catch (error) {
      setMessage('Error');
      console.error(error);
    } finally {
      setCreatingDoc(false);
    }
  };

  const sendFormPdf = async (formData) => {
    try {
      const data = parseData(formData);

      const date = new Date();
      const id = `${date.getTime()}_${user.username}`;
      const fileUrl = `document/${group}/${id}/${id}.pdf`;
      const info = { date, id, fileUrl };

      const s3Response = await Storage.get(template.fileUrl, { download: true });
      const pdf = await s3Response.Body.arrayBuffer();

      await Storage.put(info.fileUrl, pdf, {
        contentType: 'application/pdf'
      });

      const input = {
        id: info.id,
        name: formData.documento.nombre,
        description: formData.documento.descripcion || '',
        group,
        fileUrl: info.fileUrl,
        data: JSON.stringify(data),
        status: CONSTANTS.DOCUMENT.STATUS.INIT,
        documentTemplateId: template.id,
        createdAt: info.date.toISOString(),
        type: 'document',
        principal: {
          name: formData.documento.nombre,
          path: info.fileUrl,
          description: formData.documento.descripcion,
          signaturesPositions: template.signaturesPositions
        }
      };

      await GraphqlApi.createDocument(input, subgroup);
      setMessage('Ready');
      history.push('/document');
    } catch (error) {
      setMessage('Error');
      console.error(error);
    } finally {
      setCreatingDoc(false);
    }
  };

  const docxRenderError = (error) => {
    const replaceErrors = (key, value) => {
      if (value instanceof Error) {
        return Object.getOwnPropertyNames(value)
          .reduce((error, key) => {
            error[key] = value[key];
            return error;
          },
          {});
      }
      return value;
    };
    console.log(JSON.stringify({ error: error }, replaceErrors));

    if (error.properties && error.properties.errors instanceof Array) {
      const errorMessages = error.properties.errors.map((error) => {
        return error.properties.explanation;
      })
        .join('\n');
      console.log('errorMessages', errorMessages);
    }
    throw error;
  };

  const onError = (errors) => {
    setErrorList(errors);
    setCreatingDoc(false);
  };

  const handleClickSubmit = () => {
    setCreatingDoc(true);
    setMessage('Loading');
    setErrorList([]);
    submitMainFormRef.current.click();
  };

  const handleChangeForm = () => (event) => {
    if (mainFormRef.current) {
      mainFormRef.current.state.formData = Object.assign(
        mainFormRef.current.state.formData,
        { ...event.formData }
      );
    }
  };

  const handleChange = (panel) => (event, expanded) => {
    setExpanded(() => (expanded ? panel : null));
  };

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <div className={classes.mainContainer}>
      <div style={{ display: 'none' }}>
        <CustomForm
          schema={schema}
          uiSchemas={uiSchema}
          formRef={mainFormRef}
          submitFormButton={submitMainFormRef}
          formData={
            mainFormRef.current ? mainFormRef.current.state.formData : {}
          }
          onSubmit={(form) => {
            (/[.]\w{3,4}$/.exec(`public/${template.fileUrl}`)[0] === '.pdf')
              ? sendFormPdf(form.formData) : sendForm(form.formData);
          }}
          onError={(errors) => onError(errors)}
          style={{ display: 'none' }}
        />
      </div>

      {windowSize.width >= 760 ? (
        <TemplateWithGrid
          schemas={schemas}
          uiSchemas={uiSchemas}
          handleChange={handleChange}
          handleChangeForm={handleChangeForm}
          handleClickSubmit={handleClickSubmit}
          expanded={expanded}
          errors={errorList}
          formData={mainFormRef.current ? mainFormRef.current.state.formData : {}}
        />
      ) : (
        <TemplateWithDialog
          schemas={schemas}
          uiSchemas={uiSchemas}
          handleChange={handleChange}
          handleChangeForm={handleChangeForm}
          handleClickSubmit={handleClickSubmit}
          expanded={expanded}
          errors={errorList}
          formData={mainFormRef.current ? mainFormRef.current.state.formData : {}}
        />
      )}
      {creatingDoc && <LoadingCard open={creatingDoc} loading={creatingDoc} message={t(`template:${message}`)} />}
    </div>
  );
};

Template.propTypes = {
  match: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  group: PropTypes.string.isRequired,
  subgroup: PropTypes.string.isRequired

};

const mapStateToProps = (state) => {
  return {
    user: state.config.user,
    group: state.config.group,
    subgroup: state.config.subgroup
  };
};

export default connect(mapStateToProps, null)(Template);
